/* eslint-disable no-param-reassign */
import { makeAutoObservable } from 'mobx';
import { createContext } from 'react';
import Cookies from 'js-cookie';
import { darken } from 'polished';
import { toast } from 'react-toastify';
import type { Schema } from '../types/Schema';
import type { AssetList } from '../types/AssetList';
import type { TransactionList } from '../types/TransactionList';
import type { DataTypeList } from '../types/DataTypeList';
import { instance } from '../services/api';
import { Caller } from '../types/Caller';

const THEME_COLOR = '#6b63d5';

class Globals {
  definitions: Schema = {
    name: '',
    colors: [THEME_COLOR, darken(0.1, THEME_COLOR), '#FFFFFF'],
    version: '',
    orgMSP: '',
  };

  fingerprintServer: { url: string; port: string } = {
    url: 'http://localhost',
    port: '6174',
  };

  isDrawerOpen = false;

  headerHeight = '50px';

  assetList: AssetList = [];

  transactionList: TransactionList = [];

  standardTxList: TransactionList = [];

  dataTypeList: DataTypeList = [];

  baseAddress = '';

  authToken = '';

  currentOrganization: any = null;

  configServer = false;

  dynamicTxsAllowed = false;

  constructor() {
    makeAutoObservable(this);
  }

  setDynamicTxsAllowed = (config: boolean) => {
    this.dynamicTxsAllowed = config;
  };

  setConfigServer = (config: boolean) => {
    this.configServer = config;
  };

  setDefinitions = (data: Schema) => {
    data.colors = [THEME_COLOR, darken(0.1, THEME_COLOR), '#FFFFFF'];
    this.definitions = data;
  };

  setDrawerOpen = (open: boolean) => {
    this.isDrawerOpen = open;
  };

  setAssetList = (list: AssetList) => {
    this.assetList = list;
  };

  setDataTypeList = (list: DataTypeList) => {
    this.dataTypeList = list;
  };

  setTransactionList = (list: TransactionList) => {
    this.transactionList = list;
  };

  setStandardTxList = (list: TransactionList) => {
    this.standardTxList = list;
  };

  setBaseAddress = (baseAddress: string) => {
    this.baseAddress = baseAddress;
  };

  setAuthToken = (clientId: string, clientSecret: string) => {
    const credentials = `${clientId}:${clientSecret}`;
    this.authToken = `Bearer ${credentials}`;
    localStorage.setItem('@CoreBlockchain:authToken', this.authToken);
    localStorage.setItem('@CoreBlockchain:clientId', clientId);
  };

  setCurrentOrganization = (org: any) => {
    this.currentOrganization = org;
  };

  clearAuth = () => {
    this.authToken = '';
    this.currentOrganization = null;
    localStorage.removeItem('@CoreBlockchain:authToken');
    localStorage.removeItem('@CoreBlockchain:clientId');
  };

  setFingerprintServer = (url: string, port: string) => {
    this.fingerprintServer.url = url;
    this.fingerprintServer.port = port;
  };



  resetGlobals = async () => {
    let baseAddress = '';
    const restServer = localStorage.getItem('@CoreBlockchain:restServer');

    if (restServer && restServer !== '') {
      baseAddress = restServer;
    } else if (process.env.NODE_ENV === 'development')
      baseAddress = process.env.REACT_APP_BASE_URL as string;
    else {
      baseAddress = `http://${Cookies.get('base_url') || 'localhost'}:80`;
    }
    this.setBaseAddress(baseAddress);

    const authToken = localStorage.getItem('@CoreBlockchain:authToken');
    if (!authToken || authToken === 'askForToken' || authToken === '') {
      localStorage.setItem('@CoreBlockchain:authToken', 'askForToken');
      this.setConfigServer(true);
      return '';
    }

    const promises = await Promise.all([
      instance.post('/api/query/getHeader/'),
      instance.post('/api/query/getSchema/'),
      instance.post('/api/query/getTx/'),
    ]);

    const { data }: { data: Schema } = promises[0];
    const { data: assetList }: { data: AssetList } = promises[1];
    const { data: transactionList }: { data: TransactionList } = promises[2];

    const clientId = localStorage.getItem('@CoreBlockchain:clientId');
    if (clientId) {
      try {
        const orgResponse = await instance.get(`/api/organization/${clientId}`);
        if (orgResponse.data) {
          this.setCurrentOrganization(orgResponse.data);
        }
      } catch (error) {
        console.error('Failed to load current organization:', error);
      }
    }

    try {
      const dataTypes = await instance.post('/api/query/getDataTypes/');

      const dataTypeKeys: string[] = Object.keys(dataTypes.data);
      const dataTypeValues: any[] = Object.values(dataTypes.data);

      const dtList = dataTypeKeys.map((dt: string, index: number) => ({
        dataType: dt,
        acceptedFormats: dataTypeValues[index].acceptedFormats
          ? dataTypeValues[index].acceptedFormats[0]
          : 'string',
        ...(dataTypeValues[index].DropDownValues && {
          dropDownValues: dataTypeValues[index].DropDownValues,
        }),
      }));

      this.setDataTypeList(dtList);
    } catch {
      toast.error('Fail to get data types');
    }

    const txDetails = await Promise.all(
      transactionList.map((tx) =>
        instance.post('/api/query/getTx', { txName: tx.tag }),
      ),
    );

    const txList = txDetails
      .filter(
        (txDetail) =>
          txDetail.data &&
          typeof txDetail.data.metaTx === 'boolean' &&
          txDetail.data.metaTx === false,
      )
      .map((txDetail) => txDetail.data);

    this.setTransactionList(txList);

    const standardTxs = txDetails
      .filter(
        (txDetail) =>
          txDetail.data &&
          typeof txDetail.data.metaTx === 'boolean' &&
          txDetail.data.metaTx === true,
      )
      .map((txDetail) => txDetail.data);

    this.setStandardTxList(standardTxs);

    // Override colors with our theme
    data.colors = [THEME_COLOR, darken(0.1, THEME_COLOR), '#FFFFFF'];
    this.setDefinitions(data);

    if (assetList) {
      const schemas = await Promise.all(
        assetList.map((asset) =>
          instance.post('/api/query/getSchema/', { assetType: asset.tag }),
        ),
      );
      assetList.forEach((asset) => {
        const schema = schemas.find(
          (schma) => schma.data && schma.data.tag === asset.tag,
        );
        const org = this.definitions.orgMSP;

        // LabelKeys with strings and numbers
        if (schema && schema.data && schema.data.props) {
          const stringKeys = schema.data.props
            .filter(
              (item: { isKey: boolean; dataType: string }) =>
                item.isKey &&
                item.dataType !== 'number' &&
                item.dataType !== 'datetime' &&
                item.dataType !== 'boolean' &&
                item.dataType.indexOf('->') < 0 &&
                item.dataType.indexOf('[]') < 0,
            )
            .map((item: { tag: string }) => {
              return {
                assetType: asset.tag,
                dataType: 'string',
                tag: item.tag,
              };
            });

          const numberKeys = schema.data.props
            .filter(
              (item: { isKey: boolean; dataType: string }) =>
                item.isKey && item.dataType === 'number',
            )
            .map((item: { tag: string }) => {
              return {
                assetType: asset.tag,
                dataType: 'number',
                tag: item.tag,
              };
            });

          const dateKeys = schema.data.props
            .filter(
              (item: { isKey: boolean; dataType: string }) =>
                item.isKey && item.dataType === 'datetime',
            )
            .map((item: { tag: string }) => {
              return {
                assetType: asset.tag,
                dataType: 'datetime',
                tag: item.tag,
              };
            });

          numberKeys.forEach((key: any) => stringKeys.push(key));
          dateKeys.forEach((key: any) => stringKeys.push(key));

          asset.labelKeys = stringKeys;
        }

        const assetCreate = asset.writers
          ? asset.writers.some((writer: any) =>
            new RegExp(
              writer.charAt(0) !== '$' ? writer : writer.substring(1),
            ).test(org),
          )
          : true;

        asset.canCreate =
          schema && schema.data && schema.data.props
            ? schema.data.props
              .filter((prop: { isKey: boolean }) => prop.isKey)
              .every(
                (prop: any) =>
                  (prop.writers &&
                    prop.writers.some((writer: any) =>
                      new RegExp(
                        writer.charAt(0) !== '$'
                          ? writer
                          : writer.substring(1),
                      ).test(org),
                    )) ||
                  (!prop.writers && assetCreate),
              )
            : false;
      });

      assetList.forEach((asset) => {
        const schema = schemas.find(
          (item) => item.data && item.data.tag === asset.tag,
        );

        if (schema && schema.data && schema.data.props) {
          const objKeys = schema.data.props.filter(
            (item: { isKey: boolean; dataType: string }) =>
              item.isKey &&
              item.dataType.indexOf('string') < 0 &&
              item.dataType.indexOf('number') < 0 &&
              item.dataType.indexOf('boolean') < 0 &&
              item.dataType.indexOf('datetime') < 0,
          );

          objKeys.forEach((objKey: any) => {
            const objAsset = assetList.find(
              (ast) =>
                objKey.dataType === ast.tag ||
                (objKey.dataType.indexOf(ast.tag) >= 0 &&
                  objKey.dataType.indexOf('->') >= 0),
            );

            if (objAsset) {
              objAsset.labelKeys.forEach((key) => {
                asset.labelKeys.push({ ...key, assetTag: objKey.tag });
              });
            }
          });
        }
      });
    }

    this.setAssetList(assetList);

    standardTxs.forEach((tx) => {
      if (tx.tag === 'createAssetType') {
        this.setDynamicTxsAllowed(this.checkPermission(tx.callers));
      }
    });

    if (data) return data.name;
    return '';
  };

  itemLabel = (item: Record<string, any>, isKey = false): string => {
    const assetType = item['@assetType'];
    if (!assetType) return '-';
    const asset = this.assetList.find((ast) => ast.tag === assetType);
    if (!asset) return '-';

    if (item['@hash']) {
      return `PRIVATE:${item['@hash']}`;
    }

    const { labelKeys } = asset;

    let labelSum = isKey ? 'ASSET: ' : '';

    const labels = labelKeys.filter((key) => key.assetType === assetType);

    if (labels && labels.length > 0) {
      const labelStrings = labels.map((lb) => item[lb.tag]);
      if (isKey) {
        labelSum = `ASSET: ${labelStrings.reduce(
          (lb1, lb2) => `${lb1} | ${lb2} `,
        )}`;
      } else {
        labelSum = labelStrings.reduce((lb1, lb2) => `${lb1} | ${lb2} `);
      }
    }

    const objKeyTypes = labelKeys.filter((key) => key.assetType !== assetType);

    if (!objKeyTypes || objKeyTypes.length <= 0) return labelSum;

    const labelObj: { [assetTag: string]: string[] } = {};

    objKeyTypes.forEach((obj) => {
      if (obj.assetTag === obj.assetType) {
        if (!labelObj[obj.assetTag]) {
          labelObj[obj.assetTag] = [];
          if (typeof item[obj.assetTag][obj.tag] !== 'undefined') {
            labelObj[obj.assetTag].push(item[obj.assetTag][obj.tag]);
          } else if (
            labelObj[obj.assetTag].length <= 0 &&
            typeof item[obj.assetTag]['@key'] !== 'undefined'
          ) {
            labelObj[obj.assetTag].push(item[obj.assetTag]['@key']);
          }
        } else if (typeof item[obj.assetTag][obj.tag] !== 'undefined') {
          labelObj[obj.assetTag].push(item[obj.assetTag][obj.tag]);
        } else if (
          labelObj[obj.assetTag].length <= 0 &&
          typeof item[obj.assetTag]['@key'] !== 'undefined'
        ) {
          labelObj[obj.assetTag].push(item[obj.assetTag]['@key']);
        }
      }
    });

    Object.keys(labelObj).forEach((dataType: string) => {
      labelSum += ` ${dataType}:[ ${labelObj[dataType] && labelObj[dataType].length > 0
          ? labelObj[dataType].reduce((lb1, lb2) => `${lb1} | ${lb2}`)
          : dataType
        } ] `;
    });

    return labelSum;
  };

  dataTypeBase = (dataType: string) => {
    const base = this.dataTypeList.find((dt) => dt.dataType === dataType);

    if (base && base.dataType) {
      return base.dataType;
    }

    return dataType;
  };

  getDropDownValues = (dataType: string) => {
    const base = this.dataTypeList.find((dt) => dt.dataType === dataType);
    if (base?.dropDownValues) return base.dropDownValues;

    // For array types (e.g. "[]permission"), look up the inner type
    if (dataType.startsWith('[]')) {
      const innerType = dataType.substring(2);
      const innerBase = this.dataTypeList.find((dt) => dt.dataType === innerType);
      return innerBase?.dropDownValues;
    }

    return undefined;
  };

  checkPermission = (permissionList: string[] | Caller[]) => {
    const org = this.definitions.orgMSP;
    let permissions: string[] = [];
    if (permissionList && permissionList.length > 0) {
      if (typeof permissionList[0] === 'string') {
        permissions = permissionList as string[];
      } else {
        const callers = permissionList as Caller[];
        permissions = callers.map((caller) => caller.msp);
      }
    }
    return permissionList
      ? permissions.some((item) =>
        new RegExp(item.charAt(0) !== '$' ? item : item.substring(1)).test(
          org,
        ),
      )
      : true;
  };

  checkUnreachablePermission = (writers: string[], readers: string[]) => {
    if (typeof readers === 'undefined') return false;

    const canWrite = writers ? this.checkPermission(writers) : false;

    const canRead = this.checkPermission(readers);

    return !canRead && !canWrite;
  };

  hasUserPermission = (requiredPermission: string): boolean => {
    if (!this.currentOrganization || !this.currentOrganization.permissions) {
      return false;
    }
    
    const permissions = this.currentOrganization.permissions as string[];
    return permissions.includes(requiredPermission);
  };
}

const globalsStore = new Globals();
export const GlobalsContext = createContext(globalsStore);
export default globalsStore;